import { useQuery } from "@apollo/client";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useFormik } from "formik";
import { useEffect, useState } from "react";

import { getChangedFields, useDebounce } from "../../../../util";
import { normalizeInvoiceSearchTerm } from "../../../../util/parseInvoiceReference";
import Table from "../../../Table";
import {
  Avatar,
  Button,
  IconButton,
  Modal,
  Pagination,
  SearchBar,
} from "../..";
import {
  FETCH_INVOICE as fetchInvoice,
  FETCH_INVOICES as fetchInvoices,
} from "../../../../graphql/query/Invoice";

const LIMIT = 5;
const initialValues = { search: "", limit: LIMIT };

const TableCheck = props => {
  const { values, selectedValue, onChange } = props;
  return (
    <div className="relative flex w-[40px] items-start" key={values.id}>
      <div className="flex h-5 items-center">
        <input
          id={values.id}
          value={values.id}
          checked={selectedValue?.id === values.id}
          aria-describedby="comments-description"
          name="id"
          type="checkbox"
          className="h-4 w-4 rounded border-2 border-gray-800 text-gray-800 focus:ring-0"
          onChange={() => onChange(values)}
        />
      </div>
    </div>
  );
};

const nameAndCompany = props => {
  return (
    <>
      <div className="flex items-center gap-2">
        <Avatar
          size="lg"
          src={props?.values?.item?.contact?.img || props?.values?.contact?.img}
          label={
            props?.values?.item?.contact?.img ||
            props?.values?.contact?.img ||
            props?.values?.item?.contact?.name ||
            props?.values?.contact?.name
          }
        />
        <div>
          <p>
            {props?.values?.item?.contact?.name || props?.values?.contact?.name}
          </p>
          <p className="mt-1 italic text-gray-400">
            {props?.values?.item?.companyName || props?.values?.companyName}
          </p>
        </div>
      </div>
    </>
  );
};

const InvoiceSearchInput = props => {
  const { disabled, error, label, loading, onChange: setValue, value } = props;
  const [filters, setFilters] = useState(initialValues);

  const debouncedFilters = useDebounce(filters, 800);
  const skip = Object.keys(debouncedFilters).length === 0;
  const {
    data: { invoices: { edges = [], count } = {} } = {},
    loading: fetching,
  } = useQuery(fetchInvoices({ company: {} }), {
    skip,
    variables: { input: { ...debouncedFilters } },
  });
  const { data: { invoice = {} } = {}, client } = useQuery(fetchInvoice(), {
    skip: !value,
    variables: { id: value },
  });

  const [selectedValue, setSelectedValue] = useState(null);

  useEffect(() => {
    if (!value) {
      setSelectedValue(null);
      client.writeQuery({
        query: fetchInvoice(),
        data: {},
      });
    } else {
      setSelectedValue({ id: value });
    }
  }, [value]);

  const formikSearch = useFormik({
    initialValues,
  });

  const onChange = values => {
    // Users sometimes type the full display reference (e.g. "WCA123456")
    // when looking up an invoice. The server only stores the numeric part
    // on `invoice.number` and matches `search` with iLike, so strip the
    // company-code prefix when the input clearly looks like one. Other
    // searches (names, dates, partial digits) pass through unchanged.
    const normalised =
      values?.search !== undefined
        ? { ...values, search: normalizeInvoiceSearchTerm(values.search) }
        : values;
    setFilters(prevState => ({ ...prevState, ...normalised }));
  };

  const handleSave = () => {
    setValue?.(selectedValue?.id || null);
  };

  const searchBarProps = {
    name: "search",
    placeholder: "Search",
    formik: formikSearch,
    variant: "simple",
  };

  const paginationProps = {
    count,
    limit: LIMIT,
    offset: filters?.offset,
    showSteps: false,
    onChange,
    variant: "simple",
  };

  const handleOnClick = value => {
    value?.id === selectedValue?.id
      ? setSelectedValue(null)
      : setSelectedValue(value);
  };

  const tableProps = {
    headers: [
      {
        label: "",
        width: "50px",
        name: "checkbox",
        type: "custom",
        component: props => (
          <TableCheck
            {...props}
            selectedValue={selectedValue}
            onChange={handleOnClick}
          />
        ),
      },
      { label: "Date", name: "date", type: "label" },
      { label: "Invoiced From", name: "company.name", type: "label" },
      { label: "Invoice Number", name: "reference", type: "label" },
      {
        label: "Client Name & Company",
        type: "custom",
        component: props => nameAndCompany(props),
      },
    ],
    limit: LIMIT,
    loading: fetching,
    rows: edges,
    variant: "lg",
  };

  useEffect(() => {
    onChange(formikSearch?.values);
    const changedFields = getChangedFields(
      formikSearch?.values,
      formikSearch?.initialValues,
    );
    if (Object.keys(changedFields).length > 0) {
      setFilters(prevState => ({ ...prevState, offset: 0 }));
    }
  }, [formikSearch?.values]);

  const modalProps = {
    scale: "md",
    hideCloseButton: true,
    closeOnBackdrop: true,
    disabled,
    renderBody: ({ closeModal }) => {
      return (
        <div className="flex w-full min-w-0 flex-col p-2 sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <div className="flex flex-1 flex-col text-2xl font-bold">
              <div>Select {label}</div>
            </div>
            <div className="flex flex-col">
              <div className="flex flex-wrap gap-2 sm:flex-nowrap">
                <Button
                  action="default"
                  className="sm:mr-6"
                  label="Cancel"
                  onClick={() => {
                    closeModal();
                    setSelectedValue(invoice);
                  }}
                />
                <Button
                  action="default"
                  className="sm:mr-6"
                  disabled={!selectedValue}
                  label="Clear Selection"
                  onClick={() => {
                    setSelectedValue(null);
                  }}
                />
                <Button
                  label={loading ? "SELECTING" : "SELECT"}
                  disabled={loading || fetching || (!value && !selectedValue)}
                  onClick={() => {
                    closeModal();
                    handleSave();
                  }}
                />
              </div>
            </div>
          </div>
          <div className="min-w-0 pt-6 sm:pt-12">
            <div className="grid grid-cols-1 gap-4 pb-6 sm:grid-cols-2 sm:gap-10 sm:pb-12">
              <div>
                <SearchBar {...searchBarProps} />
              </div>
            </div>
            <div className="max-w-full overflow-x-auto">
              <Table {...tableProps} />
            </div>
            <div>
              <Pagination {...paginationProps} />
            </div>
          </div>
        </div>
      );
    },
  };

  return (
    <Modal {...modalProps}>
      <div className={`flex flex-1 flex-col`}>
        <div className={`block text-xs font-medium text-black`}>{label}</div>
        <div
          className={`mt-1 flex h-[38px] w-full items-center rounded border border-black focus:border-black focus:ring-0 ${disabled ? "bg-gray-100" : ""}`}
        >
          <div className={` flex-1 px-3 py-2 text-base `}>
            {invoice?.reference}
          </div>
          <IconButton variant="clean" title="Search Invoice">
            <MagnifyingGlassIcon
              className={`h-5 w-5 cursor-pointer text-gray-400`}
            />
          </IconButton>
        </div>
        {error && <small className="text-sm text-red-600">{error}</small>}
      </div>
    </Modal>
  );
};

export default InvoiceSearchInput;
